/**
 * <AuditFooter> — compact "Created / Updated by" strip for detail pages.
 *
 * Usage:
 *   <AuditFooter record={project} />
 *   <AuditFooter collection="clients" recordId={id} />   // fetches on its own
 *
 * When ``record`` is provided we just read its audit fields. If a record
 * doesn't have them embedded (older docs, tightly-projected payloads), pass
 * ``collection`` + ``recordId`` and we'll pull them from the audit endpoint.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { Clock, UserRound } from "lucide-react";

export default function AuditFooter({
  record,
  collection,
  recordId,
  className = "",
  compact = false,
  testid = "audit-footer",
}) {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(record || null);

  useEffect(() => {
    if (record) { setData(record); return; }
    if (!collection || !recordId || !activeWorkspaceId) return;
    let alive = true;
    api
      .get(`/workspaces/${activeWorkspaceId}/audit/record`, {
        params: { collection, id: recordId },
      })
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [record, collection, recordId, activeWorkspaceId]);

  if (!data) return null;
  const hasCreated = data.created_by_name || data.created_at;
  const hasUpdated = data.updated_by_name || data.updated_at;
  if (!hasCreated && !hasUpdated) return null;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground border-t border-border/60 pt-3 mt-3 ${className}`}
      data-testid={testid}
    >
      {hasCreated && (
        <span className="inline-flex items-center gap-1.5">
          <UserRound className="w-3.5 h-3.5" aria-hidden />
          <span>
            Created by{" "}
            <span className="font-medium text-foreground">
              {data.created_by_name || "Unknown"}
            </span>
            {data.created_at && (
              <>
                {" • "}
                <Clock className="inline w-3 h-3 opacity-70 mr-0.5" aria-hidden />
                {fmtRelative(data.created_at)}
              </>
            )}
          </span>
        </span>
      )}
      {hasUpdated && (
        <span className="inline-flex items-center gap-1.5">
          <span>
            Updated by{" "}
            <span className="font-medium text-foreground">
              {data.updated_by_name || "Unknown"}
            </span>
            {data.updated_at && (
              <>
                {" • "}
                <Clock className="inline w-3 h-3 opacity-70 mr-0.5" aria-hidden />
                {fmtRelative(data.updated_at)}
              </>
            )}
          </span>
        </span>
      )}
    </div>
  );
}
